import { apiError } from "../../../../../utils/formErrors";
import React, { useState, useMemo, useEffect } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router";
import TopBar from "../../../../../component/TopBar";
import CommitButton from "../../../../../component/CommitButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import FormField from "../../../../../component/Formfield";
import TypeBtn from "../../../../../component/AddRemoveButton";
import ErrorMsg from "../../../../../component/ErrorMsg";
import { WizardCard, FormSection, FieldGrid, FieldLabel, WizardActions } from "../../../../../component/wizard";
import { createTimesheet, getProjects, getTimesheetSettings } from "../../../../../services/timesheet";
import { FilterSelect } from "../../../../../component/listing";
import {
  projectOptions,
  toActivityPayload,
  filledRows,
  entryError,
  rowError,
} from "./timesheetEntry";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../theme";

import { pressable } from "../../../../../component/pressableProps";
const dateInputSx = {
  width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: "0.875rem", color: tokens.text,
  border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", padding: "11px 14px",
  background: tokens.card, outline: "none", "&:hover": { borderColor: tokens.textMuted }, "&:focus": { borderColor: tokens.primary, boxShadow: `inset 0 0 0 1px ${tokens.primary}` },
};

const emptyActivity = () => ({ date: "", projectRef: "", project: "", taskName: "", hours: "" });

const TimesheetForm = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [apiCall, setApiCall] = useState(false);
  const [error, setError] = useState("");
  // A missing period is a fact about two specific date fields, so it is said
  // beside them rather than in the line above the buttons.
  const [periodError, setPeriodError] = useState("");
  const checkPeriod = (from, to) =>
    !from || !to ? "Both dates are needed to set the period." : "";
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activities, setActivities] = useState([emptyActivity()]);
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState(null);

  /**
   * Projects and the org's entry rules.
   *
   * Neither is required to render the form, so a failure on either is not a
   * failure of the screen: without projects it falls back to a typed label,
   * and without settings there is no daily cap and no required project.
   */
  useEffect(() => {
    let active = true;
    (async () => {
      const [p, cfg] = await Promise.all([
        getProjects().catch(() => null),
        getTimesheetSettings().catch(() => null),
      ]);
      if (!active) return;
      setProjects(Array.isArray(p?.data) ? p.data : []);
      setSettings(cfg?.data || null);
    })();
    return () => { active = false; };
  }, []);

  const options = useMemo(() => projectOptions(projects), [projects]);

  /**
   * One line under a row: its error if it has one, else a note about the
   * project it points at. Errors win — a non-billable warning under a row that
   * is missing its date is answering a question nobody asked yet.
   */
  const rowMessage = (a) => {
    const err = rowError(a, {
      requireProject: Boolean(settings?.requireProject),
      maxHoursPerDay: Number(settings?.maxHoursPerDay) || 0,
      hasProjects: options.length > 0,
    });
    if (err) return { text: err, tone: tokens.danger };
    const picked = options.find((o) => o.value === a.projectRef);
    if (picked && !picked.billable) {
      return { text: "This project is not billable — the hours are recorded but not charged.", tone: tokens.textMuted };
    }
    return null;
  };
  const hasProjects = options.length > 0;

  const totalHours = useMemo(() => activities.reduce((s, a) => s + (parseFloat(a.hours) || 0), 0), [activities]);

  const setActivity = (i, field, value) => {
    setActivities((prev) => prev.map((a, idx) => (idx === i ? { ...a, [field]: value } : a)));
    setError("");
    setPeriodError("");
  };

  const submit = async (status) => {
    const period = checkPeriod(startDate, endDate);
    if (period) { setPeriodError(period); return; }
    const problem = entryError(activities, settings, hasProjects);
    if (problem) { setError(problem); return; }
    const cleaned = filledRows(activities).map((a) => toActivityPayload(a, options));

    setApiCall(true);
    try {
      const res = await createTimesheet({ startDate, endDate, activities: cleaned, status });
      if (res?.success === false) throw apiError(res, "Could not save timesheet");
      notify.success(status === "pending" ? "Timesheet submitted" : "Timesheet saved");
      navigate("/app/timesheet");
    } catch (e) {
      setError(e?.message || "Could not save timesheet");
      notify.error(e?.message || "Could not save timesheet");
    } finally {
      setApiCall(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Timesheets"} breadcrumbs={[{ label: "Timesheet", href: "/app/timesheet" }, { label: "New" }]} />
      <WizardCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>New Timesheet</Box>
          <BtnSecondary to="/app/timesheet" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        <FormSection title="Period" description="The week (or range) this timesheet covers.">
          <FieldGrid>
            <Box>
              <FieldLabel required>Start Date</FieldLabel>
              <Box component="input" type="date" value={startDate} max={endDate || undefined}
                onChange={(e) => { setStartDate(e.target.value); setError(""); setPeriodError(""); }}
                onBlur={(e) => setPeriodError(checkPeriod(e.target.value, endDate))}
                sx={periodError ? { ...dateInputSx, borderColor: tokens.danger, borderWidth: "2px" } : dateInputSx} />
            </Box>
            <Box>
              <FieldLabel required>End Date</FieldLabel>
              <Box component="input" type="date" value={endDate} min={startDate || undefined}
                onChange={(e) => { setEndDate(e.target.value); setError(""); setPeriodError(""); }}
                onBlur={(e) => setPeriodError(checkPeriod(startDate, e.target.value))}
                sx={periodError ? { ...dateInputSx, borderColor: tokens.danger, borderWidth: "2px" } : dateInputSx} />
            </Box>
          </FieldGrid>
          <ErrorMsg>{periodError}</ErrorMsg>
        </FormSection>

        <FormSection title="Activities" description={`Log work by day. Total: ${totalHours} h`}>
          <Stack spacing={1.5}>
            {activities.map((a, i) => (
              <Box key={i} sx={{ p: "1rem 1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface }}>
                <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "flex-end" }} spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel required>Date</FieldLabel>
                  <Box component="input" type="date" value={a.date} min={startDate || undefined} max={endDate || undefined}
                    onChange={(e) => setActivity(i, "date", e.target.value)} sx={dateInputSx} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel required={Boolean(settings?.requireProject) && hasProjects}>Project</FieldLabel>
                  {/*
                    A picker, not a text box. The server resolves billability and
                    the rate from `projectRef`; a typed name leaves the ref unset,
                    so every activity defaulted to billable and per-project rates
                    could never match. Free text remains only where an
                    organisation has configured no projects at all.
                  */}
                  {hasProjects ? (
                    <FilterSelect
                      value={a.projectRef}
                      onChange={(e) => setActivity(i, "projectRef", e.target.value)}
                      options={[{ value: "", label: "Select a project" }, ...options]}
                      inputProps={{ "aria-label": "Project" }}
                      fullWidth
                    />
                  ) : (
                    <FormField type="text" value={a.project} onChange={(e) => setActivity(i, "project", e.target.value)} fullwidth />
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel>Task</FieldLabel>
                  <FormField type="text" value={a.taskName} onChange={(e) => setActivity(i, "taskName", e.target.value)} fullwidth />
                </Box>
                <Box sx={{ width: { md: "6rem" } }}>
                  <FieldLabel required>Hours</FieldLabel>
                  <FormField type="number" value={a.hours} onChange={(e) => setActivity(i, "hours", e.target.value)} fullwidth />
                </Box>
                {activities.length > 1 && (
                  <Box {...pressable(() => setActivities((prev) => prev.filter((_, idx) => idx !== i)))}
                    sx={{ cursor: "pointer", color: tokens.danger, pb: "0.75rem" }}>
                    <DeleteOutlineIcon />
                  </Box>
                )}
                </Stack>
                {/* What this row is wrong about, and what the picked project
                    decides on its own — a non-billable project is not an error,
                    but it is worth knowing before the hours are logged. */}
                {rowMessage(a) && (
                  <Box sx={{ fontSize: "0.75rem", color: rowMessage(a).tone, mt: "0.375rem" }}>
                    {rowMessage(a).text}
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
          <Box sx={{ mt: "1rem" }}>
            <TypeBtn type="add" onClick={() => setActivities([...activities, emptyActivity()])}>Add Activity</TypeBtn>
          </Box>
        </FormSection>

        <ErrorMsg>{error}</ErrorMsg>
        <WizardActions>
          <BtnSecondary onClick={() => submit("incomplete")} disabled={apiCall} sx={{ borderRadius: "8px" }}>
            Save Draft
          </BtnSecondary>
          {/* One of the book's three marigold actions: submitting a timesheet
              certifies hours that become someone's pay. */}
          <CommitButton onClick={() => submit("pending")} disabled={apiCall} sx={{ borderRadius: "8px" }}>
            {apiCall ? <CircularProgress size={"1.25rem"} /> : "Submit for Approval"}
          </CommitButton>
        </WizardActions>
      </WizardCard>
    </Box>
  );
};

export default TimesheetForm;
